import Row from "./EditNotificationRow";
import "../../index.css";
import { useState, useEffect } from "react";
import EditPanel from "./EditPanel";
import Toast from "./Toast";
import Modal from "./Modal";
import ReactGA from 'react-ga4';
// import { useLocation } from "react-router-dom";
import API from '../Shared/API'; // Import the centralized API utility

const EditNotification = () => {
  // const location = useLocation();

  // active&inactive alerts
  const [alerts, setAlerts] = useState([]);
  const [inactiveAlerts, setInactiveAlerts] = useState([]);

  // NEW: Keep fetched "stateOptions" & "localityOptions" in local state
  const [stateOptions, setStateOptions] = useState([]);
  const [localityOptions, setLocalityOptions] = useState([]);

  // Whether to open edit panel
  const [editView, setEditView] = useState({
    alert: null,
    edit: false,
    type: null,
  });
  // Which tab to show
  const [tab, setTab] = useState("active-alerts");
  // Whether to show tooltip
  const [tooltip, setTooltip] = useState(false);
  // Whether to show toast
  const [toast, setToast] = useState({
    notified: false,
    state: false,
    message: "",
  });
  // Define setToast to map notified values to the Toast component
  // const setToastNotification = ({ notified, message, setToastState }) => {
  //   setToastState({ notified, message, state: true });
  // };
  // Whether to show modal
  const [modal, setModal] = useState({
    show: false,
    id: null,
  });

  // Fetch alerts from backend
  const fetchAlertData = async () => {
    try {
      console.log('Fetching alerts...');
      const response = await API.get('/user_auth/alerts/');
      const data = response.data;
      console.log('Alerts fetched:', data);
      
      setAlerts(data.filter((alert) => alert.is_notifying));
      setInactiveAlerts(data.filter((alert) => !alert.is_notifying));
    } catch (error) {
      console.error('Error fetching alerts:', error);
      alert("Could not load alerts. Please try again later.");
    }
  };

  // ----- Fetch STATE & LOCALITIES from the new endpoint -----
  const fetchLocalities = async () => {
    try {
      const response = await API.get('/user_auth/user-localities/');
      const data = response.data;
      // data.states and data.localities are already in {value, label} format.
      setStateOptions(data.states || []);
      setLocalityOptions(data.localities || []);
    } catch (error) {
      console.error("Failed to fetch localities:", error);
      alert("Could not load localities. Please try again later.");
    }
  };

  useEffect(() => {
    console.log('EditNotification mounted');
    fetchLocalities();
    fetchAlertData();
  }, []); // Only run on mount

  // Create new alert
  const createAlert = async (alert) => {
    try {
      const payload = {
        keywords: alert.keywords,
        locality: alert.locality.length ? alert.locality : [""],
        state: alert.state.length ? alert.state : [""],
        // frequency: alert.frequency,
        // channel: alert.channel,
        is_notifying: true,
        // day: alert.day,
        // time: alert.time,
        last_sent: alert.last_sent,
      };

      const response = await API.post('/user_auth/alerts/', payload);

      if (response.status === 200|| response.status === 201) {
        console.log('Alert created successfully');
        await fetchAlertData(); // Refresh the alerts list
        setEditView({ alert: null, edit: false, type: null });
        setToast({ 
          notified: "new alert success", 
          state: true,
        });
        setTab("active-alerts"); // Switch to active alerts tab
      } else {
        const errorData = await response.json();
        console.error("New alert creation failed:", errorData);

        // Aggregate error messages
        const errorMessages = Object.values(errorData.details || {})
          .flat()
          .join(" ");

        setToast({ 
          notified: "new alert error", 
          message: errorMessages || "Failed to create alert.", 
          state: true,
        });
      }
    } catch (error) {
      console.error("Error creating alert:", error);
      setToast({ 
        notified: "new alert error", 
        message: "An unexpected error occurred while creating the alert.", 
        state: true,
      });
    }
  };

  // Update alert (notify means whether it's updating is_notifying)
  const updateAlert = async (info, notify) => {
    try {
      const payload = {
        id: info.id,
        keywords: info.keywords,
        locality: info.locality,
        state: info.state,
        is_notifying: info.is_notifying,
        last_sent: info.last_sent,
      };

      const response = await API.put('/user_auth/alerts/', payload);

      if (response.status === 200 || response.status === 201) {
        console.log('Alert updated successfully');
        await fetchAlertData(); // Refresh the alerts list
        setEditView({ alert: null, edit: false, type: null });
        if (notify) {
          setToast({ 
            notified: info.is_notifying ? "activate" : "deactivate", 
            state: true,
          });
        } else {
          setToast({ 
            notified: "update alert success", 
            state: true,
          });
        }
      } else {
        const errorData = await response.json();
        console.error("Alert update failed:", errorData);
        
        // Aggregate error messages
        const errorMessages = Object.values(errorData.details || {})
          .flat()
          .join(" ");
        
        setToast({ 
          notified: "update alert error", 
          message: errorMessages || "Failed to update alert.", 
          state: true,
        });
      }
    } catch (error) {
      console.error("Error updating alert:", error);
      setToast({ 
        notified: "update alert error", 
        message: "An unexpected error occurred while updating the alert.", 
        state: true,
      });
    }
  };

  // Delete alert
  const deleteAlert = async (id) => {
    try {
      const payload = { id };

      const response = await API.delete('/user_auth/alerts/', { data: payload });
  
      if (response.status === 200 || response.status === 201) {
        console.log('Alert deleted successfully');
        setModal({ show: false, id: null });
        await fetchAlertData(); // Refresh the alerts list
        setToast({ 
          notified: "delete alert success", 
          state: true,
        });
      } else {
        const errorData = await response.json();
        console.error("Alert deletion failed:", errorData);
        
        // Aggregate error messages
        const errorMessages = Object.values(errorData.details || {})
          .flat()
          .join(" ");
        
        setToast({ 
          notified: "delete alert error", 
          message: errorMessages || "Failed to delete alert.", 
          state: true,
        });
      }
    } catch (error) {
      console.error("Error deleting alert:", error);
      setToast({ 
        notified: "delete alert error.", 
        message: "An unexpected error occurred while deleting the alert.", 
        state: true,
      });
    }
  };

  const handleClickAddAlert = () => {
    ReactGA.event({
      category: "alerts",
      action: "click-new-alert",
      label: "click-new-alert-settings_page",
    });
  };

  return (
    <div className="overflow-hidden bg-white sm:rounded-lg md:ml-64 relative">
      {/* toast for activate/deactivate */}
      {toast.state && <Toast notified={toast.notified} setToast={setToast} />}
      {modal.show && (
        <Modal setModal={setModal} id={modal.id} deleteAlert={deleteAlert} />
      )}
      <div className="flex justify-between content-center">
        {/* Interactive tabs */}
        <ul className="mb-5 flex list-none flex-col flex-wrap border-b-0 pl-0 ml-16 md:flex-row">
          <li className="flex-auto text-center">
            <button
              type="button"
              className={`focus:border-transparent my-2 block border-x-0 border-t-0 border-b-2 border-transparent px-7 pt-4 pb-3.5 text-s font-medium leading-tight ${
                tab === "active-alerts" ? "border-theme-darkblue text-theme-darkblue" : "text-theme-darkgray"
              }`}
              onClick={() => setTab("active-alerts")}
              role="tab"
              aria-controls="active"
              aria-selected={tab === "active-alerts"}
            >
              Active Alerts
            </button>
          </li>
          <li className="flex-auto text-center">
            <button
              type="button"
              className={`focus:border-transparent my-2 block border-x-0 border-t-0 border-b-2 border-transparent px-7 pt-4 pb-3.5 text-s font-medium leading-tight ${
                tab === "inactive-alerts" ? "border-theme-darkblue text-theme-darkblue" : "text-theme-darkgray"
              }`}
              onClick={() => setTab("inactive-alerts")}
              role="tab"
              aria-controls="inactive"
              aria-selected={tab === "inactive-alerts"}
            >
              Inactive Alerts
            </button>
          </li>
        </ul>
        {/* Add alert button */}
        <button
          className="w-32"
          href="/search"
          onMouseEnter={() => setTooltip(true)}
          onMouseLeave={() => setTooltip(false)}
          onClick={handleClickAddAlert}
        >
          <a href="/search">
            <svg
              width="45"
              height="45"
              viewBox="0 0 55 55"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M28 5.25C15.435 5.25 5.25 15.435 5.25 28C5.25 40.565 15.435 50.75 28 50.75C40.565 50.75 50.75 40.565 50.75 28C50.75 15.435 40.565 5.25 28 5.25ZM29.75 21C29.75 20.5359 29.5656 20.0908 29.2374 19.7626C28.9092 19.4344 28.4641 19.25 28 19.25C27.5359 19.25 27.0908 19.4344 26.7626 19.7626C26.4344 20.0908 26.25 20.5359 26.25 21V26.25H21C20.5359 26.25 20.0908 26.4344 19.7626 26.7626C19.4344 27.0908 19.25 27.5359 19.25 28C19.25 28.4641 19.4344 28.9092 19.7626 29.2374C20.0908 29.5656 20.5359 29.75 21 29.75H26.25V35C26.25 35.4641 26.4344 35.9092 26.7626 36.2374C27.0908 36.5656 27.5359 36.75 28 36.75C28.4641 36.75 28.9092 36.5656 29.2374 36.2374C29.5656 35.9092 29.75 35.4641 29.75 35V29.75H35C35.4641 29.75 35.9092 29.5656 36.2374 29.2374C36.5656 28.9092 36.75 28.4641 36.75 28C36.75 27.5359 36.5656 27.0908 36.2374 26.7626C35.9092 26.4344 35.4641 26.25 35 26.25H29.75V21Z"
                fill="#9BC8E3"
              />
            </svg>
          </a>
        </button>
      </div>
      {/* Tooltip */}
      <div
        id="tooltip-right"
        className={`absolute z-10 inline-block px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-sm top-15 right-10 ${
          !tooltip && "invisible"
        }`}
      >
        If you're looking to make an alert; please click here! <br />
        It'll bring you back to the Search Page.
      </div>
      {/* edit panel */}
      {editView.edit && (
        <EditPanel
          editView={editView}
          setEdit={setEditView}
          updateAlert={updateAlert}
          createAlert={createAlert}
        />
      )}
      {/* Active alert table */}
      <div
        className={`px-7 py-5 sm:py-3 sm:px-3 flex justify-between content-center flex-col overflow-x-auto w-full ${
          tab === "active-alerts" ? "" : "hidden"
        }`}
        id="active-alerts"
      >
        <table className="w-full text-sm text-left text-gray-500 overflow-x-auto border-collapse">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3 w-1/12"></th>
              <th scope="col" className="px-6 py-3 w-1/3">
                Keywords
              </th>
              <th scope="col" className="px-6 py-3 w-1/6">
                Locality
              </th>
              <th scope="col" className="px-6 py-3 w-1/6">
                State
              </th>
              <th scope="col" className="px-6 py-3 w-1/6">
                Last sent
              </th>
              <th scope="col" className="px-6 py-3 w-1/12"></th>
              <th scope="col" className="px-6 py-3 w-1/12"></th>
              <th scope="col" className="px-6 py-3 w-1/12"></th>
            </tr>
          </thead>
          <tbody>
            {alerts &&
              alerts.map((alert) => {
                return (
                  <Row
                  key={alert.id}
                  {...alert}
                  // Now pass the fetched states/localities to Row
                  stateOptions={stateOptions}
                  localityOptions={localityOptions}
                  setEdit={setEditView}
                  updateAlert={updateAlert}
                  deleteAlert={deleteAlert}
                  setModal={setModal}
                  active={true}
                />
                );
              })}
          </tbody>
        </table>
        {alerts.length === 0 && (
          <div className="flex flex-col justify-center items-center h-48">
            <div className="justify-center pb-2">
              <svg
                width="67"
                height="44"
                viewBox="0 0 67 44"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_746_244)">
                  <path
                    d="M6.69839 30.0677H7.60044C7.71732 30.0701 7.82862 30.1184 7.91044 30.2023C7.99227 30.2862 8.03809 30.399 8.03806 30.5165C8.03806 30.7642 7.84604 30.9653 7.60044 30.9653H6.69839V31.8718C6.69605 31.9893 6.64797 32.1011 6.56448 32.1833C6.48098 32.2656 6.36873 32.3116 6.25183 32.3116C6.19367 32.3125 6.13591 32.3019 6.08189 32.2802C6.02787 32.2586 5.97866 32.2264 5.93712 32.1855C5.89558 32.1445 5.86254 32.0958 5.8399 32.0419C5.81727 32.0419C5.8055 31.9881 5.80527 31.9302 5.80527 31.8718V30.9653H4.90322C4.78634 30.9629 4.67504 30.9146 4.59321 30.8307C4.51139 30.7468 4.46557 30.634 4.46559 30.5165C4.46559 30.2688 4.65761 30.0677 4.90322 30.0677H5.80527V29.1612C5.80761 29.0438 5.85569 28.9319 5.93918 28.8497C6.02267 28.7674 6.13492 28.7214 6.25183 28.7214C6.49833 28.7214 6.69839 28.9144 6.69839 29.1612V30.0677ZM65.6442 26.4776V25.571C65.644 25.5126 65.6322 25.4548 65.6096 25.4009C65.5869 25.3471 65.5539 25.2983 65.5123 25.2574C65.4708 25.2165 65.4216 25.1843 65.3676 25.1626C65.3135 25.141 65.2558 25.1303 65.1976 25.1312C65.0807 25.1312 64.9685 25.1773 64.885 25.2595C64.8015 25.3417 64.7534 25.4536 64.7511 25.571V26.4776H63.849C63.7909 26.4778 63.7333 26.4896 63.6798 26.5124C63.6262 26.5351 63.5776 26.5683 63.5369 26.6101C63.4962 26.6518 63.4642 26.7013 63.4426 26.7556C63.4211 26.8098 63.4105 26.8679 63.4114 26.9263C63.4114 27.1669 63.6079 27.3751 63.849 27.3751H64.7511V28.2816C64.7511 28.5284 64.9511 28.7214 65.1976 28.7214C65.3145 28.7214 65.4268 28.6754 65.5103 28.5932C65.5938 28.5109 65.6419 28.3991 65.6442 28.2816V27.3751H66.5462C66.7918 27.3751 66.9839 27.1741 66.9839 26.9263C66.9839 26.8088 66.9381 26.696 66.8562 26.6121C66.7744 26.5282 66.6631 26.4799 66.5462 26.4776H65.6442ZM33.199 41.287H10.7147C10.5975 41.2872 10.4815 41.264 10.3732 41.2189C10.265 41.1738 10.1666 41.1077 10.0839 41.0243C10.0011 40.9409 9.93562 40.8418 9.89107 40.7329C9.84652 40.624 9.82383 40.5073 9.8243 40.3895C9.8243 39.8941 10.2289 39.492 10.7147 39.492H14.765C14.4524 38.9476 14.2885 38.3299 14.2899 37.7013V11.6636C14.2901 11.1921 14.3828 10.7253 14.5627 10.2898C14.7426 9.85436 15.0062 9.45881 15.3383 9.12579C15.6704 8.79277 16.0646 8.52881 16.4983 8.349C16.932 8.1692 17.3968 8.07707 17.8659 8.0779H20.5417V7.17587C20.542 6.70437 20.6347 6.23755 20.8146 5.80209C20.9945 5.36664 21.258 4.97109 21.5901 4.63807C21.9223 4.30505 22.3164 4.04109 22.7502 3.86128C23.1839 3.68148 23.6486 3.58935 24.1178 3.59018H45.5455C46.0147 3.58912 46.4795 3.68108 46.9133 3.8608C47.3472 4.04052 47.7414 4.30447 48.0736 4.63754C48.4058 4.97061 48.6693 5.36625 48.8491 5.80181C49.029 6.23738 49.1215 6.70431 49.1215 7.17587V33.2136C49.1215 33.8661 48.9482 34.4774 48.6455 35.0042H53.5862C54.0801 35.0042 54.4802 35.4027 54.4802 35.9018C54.4802 36.3972 54.0828 36.7993 53.5871 36.7993H42.8697V37.7013C42.8697 38.3539 42.6964 38.9651 42.3936 39.492H42.8724C43.3636 39.492 43.7628 39.8905 43.7628 40.3895C43.7628 40.8849 43.3582 41.287 42.8724 41.287H38.1737C38.5818 41.9746 38.4934 42.8757 37.8995 43.4726C37.7339 43.6397 37.5371 43.7724 37.3204 43.8629C37.1037 43.9534 36.8713 44 36.6366 44C36.4019 44 36.1695 43.9534 35.9528 43.8629C35.7361 43.7724 35.5393 43.6397 35.3737 43.4726L33.199 41.287ZM36.4642 39.492H39.3043C40.2868 39.492 41.0834 38.6931 41.0834 37.6924V11.6214C41.0834 10.6521 40.3118 9.87299 39.3597 9.87299H17.7998C16.8478 9.87299 16.0761 10.6556 16.0761 11.6214V37.6924C16.0761 38.686 16.8799 39.492 17.8543 39.492H31.4119L30.9528 39.0297C30.7302 38.8072 30.5701 38.5296 30.4885 38.225C30.407 37.9203 30.4069 37.5994 30.4884 37.2948L29.6453 36.4466C28.3789 37.3939 26.8029 37.8249 25.2335 37.653C23.6641 37.4811 22.2174 36.719 21.1839 35.5197C20.1504 34.3204 19.6065 32.7727 19.6613 31.1871C19.7161 29.6016 20.3656 28.0955 21.4795 26.9712C22.5965 25.8446 24.0971 25.1857 25.6786 25.1273C27.26 25.0689 28.8047 25.6155 30.001 26.6566C31.1972 27.6978 31.9561 29.1561 32.1244 30.7375C32.2928 32.3188 31.8581 33.9055 30.9081 35.1775L31.7521 36.0256C32.0552 35.9434 32.3746 35.9432 32.6777 36.025C32.9809 36.1068 33.2572 36.2677 33.4785 36.4915L36.4642 39.492ZM42.8697 35.0042H45.5571C46.0293 35.0019 46.4815 34.8117 46.8147 34.4754C47.1479 34.139 47.335 33.6838 47.3353 33.2091V7.18035C47.3353 6.18588 46.5386 5.38527 45.5571 5.38527H24.1053C23.6331 5.38787 23.1812 5.57812 22.8482 5.91446C22.5152 6.25079 22.3282 6.70589 22.328 7.18035V8.0779H39.2936C39.7629 8.07684 40.2277 8.1688 40.6615 8.34852C41.0953 8.52824 41.4896 8.79219 41.8218 9.12526C42.154 9.45833 42.4175 9.85397 42.5973 10.2895C42.7771 10.7251 42.8697 11.192 42.8697 11.6636V35.0042ZM11.3873 2.01947H12.718C13.0931 2.01947 13.3968 2.31836 13.3968 2.69263C13.3968 3.06422 13.0931 3.36579 12.718 3.36579H11.3873V4.70313C11.3873 5.0801 11.0898 5.38527 10.7174 5.38527C10.6289 5.38468 10.5413 5.36657 10.4597 5.33197C10.3781 5.29737 10.3041 5.24696 10.2419 5.18362C10.1797 5.12028 10.1305 5.04524 10.0972 4.9628C10.0639 4.88036 10.047 4.79212 10.0476 4.70313V3.36579H8.71683C8.62822 3.36662 8.54031 3.34984 8.45817 3.31641C8.37603 3.28299 8.30127 3.23358 8.2382 3.17102C8.17512 3.10847 8.12496 3.034 8.09062 2.9519C8.05627 2.8698 8.03841 2.78169 8.03806 2.69263C8.03806 2.32105 8.34172 2.01947 8.71683 2.01947H10.0476V0.682134C10.0476 0.305165 10.345 0 10.7174 0C11.0872 0 11.3873 0.305165 11.3873 0.682134V2.01947ZM0.893118 40.3895C0.893118 39.8941 1.2977 39.492 1.78356 39.492H7.14763C7.63884 39.492 8.03806 39.8905 8.03806 40.3895C8.03806 40.8849 7.63348 41.287 7.14763 41.287H1.78356C1.66636 41.2872 1.5503 41.264 1.44204 41.2189C1.33378 41.1738 1.23545 41.1077 1.15271 41.0243C1.06997 40.9409 1.00444 40.8418 0.959888 40.7329C0.915339 40.624 0.892648 40.5073 0.893118 40.3895ZM29.0585 34.5878C29.4732 34.171 29.8022 33.6762 30.0266 33.1317C30.2511 32.5871 30.3666 32.0035 30.3666 31.4141C30.3666 30.8246 30.2511 30.241 30.0266 29.6964C29.8022 29.1519 29.4732 28.6571 29.0585 28.2403C28.6438 27.8236 28.1514 27.493 27.6096 27.2674C27.0677 27.0418 26.4869 26.9257 25.9004 26.9257C25.3139 26.9257 24.7332 27.0418 24.1913 27.2674C23.6494 27.493 23.1571 27.8236 22.7424 28.2403C21.9048 29.0821 21.4343 30.2237 21.4343 31.4141C21.4343 32.6044 21.9048 33.746 22.7424 34.5878C23.5799 35.4295 24.7159 35.9024 25.9004 35.9024C27.0849 35.9024 28.2209 35.4295 29.0585 34.5878ZM19.6486 13.4632C19.6486 13.2253 19.7426 12.9971 19.9099 12.8288C20.0772 12.6605 20.3041 12.5659 20.5408 12.5656H30.366C30.6029 12.5656 30.8301 12.6602 30.9976 12.8285C31.165 12.9968 31.2591 13.2251 31.2591 13.4632C31.2591 13.7012 31.165 13.9295 30.9976 14.0978C30.8301 14.2661 30.6029 14.3607 30.366 14.3607H20.5417C20.4243 14.3611 20.308 14.3381 20.1995 14.2931C20.091 14.2481 19.9924 14.182 19.9094 14.0986C19.8264 14.0152 19.7606 13.9161 19.7159 13.8071C19.6711 13.698 19.6482 13.5811 19.6486 13.4632ZM19.6486 17.9509C19.6486 17.4554 20.046 17.0533 20.5355 17.0533H34.8379C35.3273 17.0533 35.7247 17.4519 35.7247 17.9509C35.7247 18.4463 35.3273 18.8484 34.8379 18.8484H20.5355C20.4185 18.8483 20.3027 18.825 20.1948 18.7798C20.0868 18.7345 19.9888 18.6683 19.9064 18.5849C19.824 18.5015 19.7588 18.4026 19.7145 18.2937C19.6703 18.1849 19.6479 18.0684 19.6486 17.9509ZM19.6486 22.4386C19.6486 21.9432 20.0469 21.5411 20.5399 21.5411H26.7953C27.2874 21.5411 27.6867 21.9396 27.6867 22.4386C27.6867 22.9341 27.2883 23.3362 26.7953 23.3362H20.5399C20.4227 23.3364 20.3065 23.3133 20.1981 23.2683C20.0898 23.2232 19.9913 23.1571 19.9085 23.0737C19.8257 22.9903 19.7601 22.8912 19.7155 22.7822C19.6709 22.6732 19.6481 22.5565 19.6486 22.4386ZM2.67935 17.0533C2.3275 17.0533 1.97908 16.9837 1.65401 16.8484C1.32894 16.7131 1.03357 16.5147 0.784765 16.2647C0.535964 16.0147 0.338604 15.7178 0.203954 15.3911C0.0693036 15.0645 0 14.7143 0 14.3607C0 14.0071 0.0693036 13.657 0.203954 13.3303C0.338604 13.0036 0.535964 12.7068 0.784765 12.4567C1.03357 12.2067 1.32894 12.0084 1.65401 11.873C1.97908 11.7377 2.3275 11.6681 2.67935 11.6681C3.38996 11.6681 4.07147 11.9518 4.57394 12.4567C5.07642 12.9617 5.35871 13.6466 5.35871 14.3607C5.35871 15.0748 5.07642 15.7597 4.57394 16.2647C4.07147 16.7697 3.38996 17.0533 2.67935 17.0533ZM2.67935 15.707C2.8553 15.7071 3.02954 15.6723 3.1921 15.6046C3.35467 15.537 3.50238 15.4378 3.6268 15.3128C3.75123 15.1878 3.84993 15.0393 3.91727 14.876C3.98461 14.7126 4.01927 14.5375 4.01927 14.3607C4.01927 14.1839 3.98461 14.0088 3.91727 13.8454C3.84993 13.6821 3.75123 13.5337 3.6268 13.4086C3.50238 13.2836 3.35467 13.1845 3.1921 13.1168C3.02954 13.0492 2.8553 13.0144 2.67935 13.0144C2.32409 13.0145 1.9834 13.1563 1.73221 13.4088C1.48103 13.6613 1.33992 14.0037 1.33992 14.3607C1.33992 14.7177 1.48103 15.0601 1.73221 15.3126C1.9834 15.5651 2.32409 15.707 2.67935 15.707ZM58.0527 11.6681C57.3421 11.6681 56.6606 11.3844 56.1581 10.8794C55.6556 10.3745 55.3733 9.68957 55.3733 8.97544C55.3733 8.26131 55.6556 7.57643 56.1581 7.07146C56.6606 6.5665 57.3421 6.28281 58.0527 6.28281C58.7633 6.28281 59.4448 6.5665 59.9473 7.07146C60.4498 7.57643 60.732 8.26131 60.732 8.97544C60.732 9.68957 60.4498 10.3745 59.9473 10.8794C59.4448 11.3844 58.7633 11.6681 58.0527 11.6681ZM58.0527 10.3218C58.2286 10.3218 58.4029 10.287 58.5654 10.2193C58.728 10.1517 58.8757 10.0525 59.0001 9.92752C59.1246 9.8025 59.2233 9.65407 59.2906 9.49071C59.3579 9.32735 59.3926 9.15226 59.3926 8.97544C59.3926 8.79862 59.3579 8.62353 59.2906 8.46017C59.2233 8.29682 59.1246 8.14839 59.0001 8.02337C58.8757 7.89835 58.728 7.79918 58.5654 7.73154C58.4029 7.6639 58.2286 7.6291 58.0527 7.62913C57.6974 7.62919 57.3567 7.77106 57.1055 8.02354C56.8544 8.27602 56.7132 8.61842 56.7132 8.97544C56.7132 9.33247 56.8544 9.67487 57.1055 9.92735C57.3567 10.1798 57.6974 10.3217 58.0527 10.3218Z"
                    fill="#C4C4C4"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_746_244">
                    <rect width="67" height="44" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className="text-center">
              <h1 className="text-m font-bold text-gray-500">
                You don't have active alerts
              </h1>
              <p className="text-gray-500 text-m">
                Go to{" "}
                <a href="/search" className="underline decoration-solid">
                  Search
                </a>{" "}
                page to create an alert, or check your inactive alerts
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Inactive alert table */}
      <div
        className={`px-7 py-5 sm:py-3 sm:px-3 flex justify-between content-center flex-col overflow-x-auto w-full ${
          tab === "inactive-alerts" ? "" : "hidden"
        }`}
        id="inactive-alerts"
      >
        <table className="w-full text-sm text-left text-gray-500 border-collapse">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3 w-1/6">
                Keywords
              </th>
              <th scope="col" className="px-6 py-3 w-1/6">
                Locality
              </th>
              <th scope="col" className="px-6 py-3 w-1/6">
                State
              </th>
              {/* For Slack Implementation */}
              {/* <th scope="col" className="px-6 py-3 w-1/6">
                Channel
              </th> */}
              {/* <th scope="col" className="px-6 py-3 w-1/6">
                Frequency
              </th> */}
              <th scope="col" className="px-6 py-3 w-1/12"></th>
              <th scope="col" className="px-6 py-3 w-1/12"></th>
            </tr>
          </thead>
          <tbody>
            {inactiveAlerts &&
              inactiveAlerts.map((alert) => {
                return (
                  <Row
                  key={alert.id}
                  {...alert}
                  // Pass fetched arrays as well
                  stateOptions={stateOptions}
                  localityOptions={localityOptions}
                  deleteAlert={deleteAlert}
                  updateAlert={updateAlert}
                  setModal={setModal}
                  active={false}
                />
                );
              })}
          </tbody>
        </table>
        {inactiveAlerts.length === 0 && (
          <div className="flex flex-col justify-center items-center h-44">
            <div className="justify-center pb-2">
              <svg
                width="67"
                height="44"
                viewBox="0 0 67 44"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_746_244)">
                  <path
                    d="M6.69839 30.0677H7.60044C7.71732 30.0701 7.82862 30.1184 7.91044 30.2023C7.99227 30.2862 8.03809 30.399 8.03806 30.5165C8.03806 30.7642 7.84604 30.9653 7.60044 30.9653H6.69839V31.8718C6.69605 31.9893 6.64797 32.1011 6.56448 32.1833C6.48098 32.2656 6.36873 32.3116 6.25183 32.3116C6.19367 32.3125 6.13591 32.3019 6.08189 32.2802C6.02787 32.2586 5.97866 32.2264 5.93712 32.1855C5.89558 32.1445 5.86254 32.0958 5.8399 32.0419C5.81727 32.0419C5.8055 31.9881 5.80527 31.9302 5.80527 31.8718V30.9653H4.90322C4.78634 30.9629 4.67504 30.9146 4.59321 30.8307C4.51139 30.7468 4.46557 30.634 4.46559 30.5165C4.46559 30.2688 4.65761 30.0677 4.90322 30.0677H5.80527V29.1612C5.80761 29.0438 5.85569 28.9319 5.93918 28.8497C6.02267 28.7674 6.13492 28.7214 6.25183 28.7214C6.49833 28.7214 6.69839 28.9144 6.69839 29.1612V30.0677ZM65.6442 26.4776V25.571C65.644 25.5126 65.6322 25.4548 65.6096 25.4009C65.5869 25.3471 65.5539 25.2983 65.5123 25.2574C65.4708 25.2165 65.4216 25.1843 65.3676 25.1626C65.3135 25.141 65.2558 25.1303 65.1976 25.1312C65.0807 25.1312 64.9685 25.1773 64.885 25.2595C64.8015 25.3417 64.7534 25.4536 64.7511 25.571V26.4776H63.849C63.7909 26.4778 63.7333 26.4896 63.6798 26.5124C63.6262 26.5351 63.5776 26.5683 63.5369 26.6101C63.4962 26.6518 63.4642 26.7013 63.4426 26.7556C63.4211 26.8098 63.4105 26.8679 63.4114 26.9263C63.4114 27.1669 63.6079 27.3751 63.849 27.3751H64.7511V28.2816C64.7511 28.5284 64.9511 28.7214 65.1976 28.7214C65.3145 28.7214 65.4268 28.6754 65.5103 28.5932C65.5938 28.5109 65.6419 28.3991 65.6442 28.2816V27.3751H66.5462C66.7918 27.3751 66.9839 27.1741 66.9839 26.9263C66.9839 26.8088 66.9381 26.696 66.8562 26.6121C66.7744 26.5282 66.6631 26.4799 66.5462 26.4776H65.6442ZM33.199 41.287H10.7147C10.5975 41.2872 10.4815 41.264 10.3732 41.2189C10.265 41.1738 10.1666 41.1077 10.0839 41.0243C10.0011 40.9409 9.93562 40.8418 9.89107 40.7329C9.84652 40.624 9.82383 40.5073 9.8243 40.3895C9.8243 39.8941 10.2289 39.492 10.7147 39.492H14.765C14.4524 38.9476 14.2885 38.3299 14.2899 37.7013V11.6636C14.2901 11.1921 14.3828 10.7253 14.5627 10.2898C14.7426 9.85436 15.0062 9.45881 15.3383 9.12579C15.6704 8.79277 16.0646 8.52881 16.4983 8.349C16.932 8.1692 17.3968 8.07707 17.8659 8.0779H20.5417V7.17587C20.542 6.70437 20.6347 6.23755 20.8146 5.80209C20.9945 5.36664 21.258 4.97109 21.5901 4.63807C21.9223 4.30505 22.3164 4.04109 22.7502 3.86128C23.1839 3.68148 23.6486 3.58935 24.1178 3.59018H45.5455C46.0147 3.58912 46.4795 3.68108 46.9133 3.8608C47.3472 4.04052 47.7414 4.30447 48.0736 4.63754C48.4058 4.97061 48.6693 5.36625 48.8491 5.80181C49.029 6.23738 49.1215 6.70431 49.1215 7.17587V33.2136C49.1215 33.8661 48.9482 34.4774 48.6455 35.0042H53.5862C54.0801 35.0042 54.4802 35.4027 54.4802 35.9018C54.4802 36.3972 54.0828 36.7993 53.5871 36.7993H42.8697V37.7013C42.8697 38.3539 42.6964 38.9651 42.3936 39.492H42.8724C43.3636 39.492 43.7628 39.8905 43.7628 40.3895C43.7628 40.8849 43.3582 41.287 42.8724 41.287H38.1737C38.5818 41.9746 38.4934 42.8757 37.8995 43.4726C37.7339 43.6397 37.5371 43.7724 37.3204 43.8629C37.1037 43.9534 36.8713 44 36.6366 44C36.4019 44 36.1695 43.9534 35.9528 43.8629C35.7361 43.7724 35.5393 43.6397 35.3737 43.4726L33.199 41.287ZM36.4642 39.492H39.3043C40.2868 39.492 41.0834 38.6931 41.0834 37.6924V11.6214C41.0834 10.6521 40.3118 9.87299 39.3597 9.87299H17.7998C16.8478 9.87299 16.0761 10.6556 16.0761 11.6214V37.6924C16.0761 38.686 16.8799 39.492 17.8543 39.492H31.4119L30.9528 39.0297C30.7302 38.8072 30.5701 38.5296 30.4885 38.225C30.407 37.9203 30.4069 37.5994 30.4884 37.2948L29.6453 36.4466C28.3789 37.3939 26.8029 37.8249 25.2335 37.653C23.6641 37.4811 22.2174 36.719 21.1839 35.5197C20.1504 34.3204 19.6065 32.7727 19.6613 31.1871C19.7161 29.6016 20.3656 28.0955 21.4795 26.9712C22.5965 25.8446 24.0971 25.1857 25.6786 25.1273C27.26 25.0689 28.8047 25.6155 30.001 26.6566C31.1972 27.6978 31.9561 29.1561 32.1244 30.7375C32.2928 32.3188 31.8581 33.9055 30.9081 35.1775L31.7521 36.0256C32.0552 35.9434 32.3746 35.9432 32.6777 36.025C32.9809 36.1068 33.2572 36.2677 33.4785 36.4915L36.4642 39.492ZM42.8697 35.0042H45.5571C46.0293 35.0019 46.4815 34.8117 46.8147 34.4754C47.1479 34.139 47.335 33.6838 47.3353 33.2091V7.18035C47.3353 6.18588 46.5386 5.38527 45.5571 5.38527H24.1053C23.6331 5.38787 23.1812 5.57812 22.8482 5.91446C22.5152 6.25079 22.3282 6.70589 22.328 7.18035V8.0779H39.2936C39.7629 8.07684 40.2277 8.1688 40.6615 8.34852C41.0953 8.52824 41.4896 8.79219 41.8218 9.12526C42.154 9.45833 42.4175 9.85397 42.5973 10.2895C42.7771 10.7251 42.8697 11.192 42.8697 11.6636V35.0042ZM11.3873 2.01947H12.718C13.0931 2.01947 13.3968 2.31836 13.3968 2.69263C13.3968 3.06422 13.0931 3.36579 12.718 3.36579H11.3873V4.70313C11.3873 5.0801 11.0898 5.38527 10.7174 5.38527C10.6289 5.38468 10.5413 5.36657 10.4597 5.33197C10.3781 5.29737 10.3041 5.24696 10.2419 5.18362C10.1797 5.12028 10.1305 5.04524 10.0972 4.9628C10.0639 4.88036 10.047 4.79212 10.0476 4.70313V3.36579H8.71683C8.62822 3.36662 8.54031 3.34984 8.45817 3.31641C8.37603 3.28299 8.30127 3.23358 8.2382 3.17102C8.17512 3.10847 8.12496 3.034 8.09062 2.9519C8.05627 2.8698 8.03841 2.78169 8.03806 2.69263C8.03806 2.32105 8.34172 2.01947 8.71683 2.01947H10.0476V0.682134C10.0476 0.305165 10.345 0 10.7174 0C11.0872 0 11.3873 0.305165 11.3873 0.682134V2.01947ZM0.893118 40.3895C0.893118 39.8941 1.2977 39.492 1.78356 39.492H7.14763C7.63884 39.492 8.03806 39.8905 8.03806 40.3895C8.03806 40.8849 7.63348 41.287 7.14763 41.287H1.78356C1.66636 41.2872 1.5503 41.264 1.44204 41.2189C1.33378 41.1738 1.23545 41.1077 1.15271 41.0243C1.06997 40.9409 1.00444 40.8418 0.959888 40.7329C0.915339 40.624 0.892648 40.5073 0.893118 40.3895ZM29.0585 34.5878C29.4732 34.171 29.8022 33.6762 30.0266 33.1317C30.2511 32.5871 30.3666 32.0035 30.3666 31.4141C30.3666 30.8246 30.2511 30.241 30.0266 29.6964C29.8022 29.1519 29.4732 28.6571 29.0585 28.2403C28.6438 27.8236 28.1514 27.493 27.6096 27.2674C27.0677 27.0418 26.4869 26.9257 25.9004 26.9257C25.3139 26.9257 24.7332 27.0418 24.1913 27.2674C23.6494 27.493 23.1571 27.8236 22.7424 28.2403C21.9048 29.0821 21.4343 30.2237 21.4343 31.4141C21.4343 32.6044 21.9048 33.746 22.7424 34.5878C23.5799 35.4295 24.7159 35.9024 25.9004 35.9024C27.0849 35.9024 28.2209 35.4295 29.0585 34.5878ZM19.6486 13.4632C19.6486 13.2253 19.7426 12.9971 19.9099 12.8288C20.0772 12.6605 20.3041 12.5659 20.5408 12.5656H30.366C30.6029 12.5656 30.8301 12.6602 30.9976 12.8285C31.165 12.9968 31.2591 13.2251 31.2591 13.4632C31.2591 13.7012 31.165 13.9295 30.9976 14.0978C30.8301 14.2661 30.6029 14.3607 30.366 14.3607H20.5417C20.4243 14.3611 20.308 14.3381 20.1995 14.2931C20.091 14.2481 19.9924 14.182 19.9094 14.0986C19.8264 14.0152 19.7606 13.9161 19.7159 13.8071C19.6711 13.698 19.6482 13.5811 19.6486 13.4632ZM19.6486 17.9509C19.6486 17.4554 20.046 17.0533 20.5355 17.0533H34.8379C35.3273 17.0533 35.7247 17.4519 35.7247 17.9509C35.7247 18.4463 35.3273 18.8484 34.8379 18.8484H20.5355C20.4185 18.8483 20.3027 18.825 20.1948 18.7798C20.0868 18.7345 19.9888 18.6683 19.9064 18.5849C19.824 18.5015 19.7588 18.4026 19.7145 18.2937C19.6703 18.1849 19.6479 18.0684 19.6486 17.9509ZM19.6486 22.4386C19.6486 21.9432 20.0469 21.5411 20.5399 21.5411H26.7953C27.2874 21.5411 27.6867 21.9396 27.6867 22.4386C27.6867 22.9341 27.2883 23.3362 26.7953 23.3362H20.5399C20.4227 23.3364 20.3065 23.3133 20.1981 23.2683C20.0898 23.2232 19.9913 23.1571 19.9085 23.0737C19.8257 22.9903 19.7601 22.8912 19.7155 22.7822C19.6709 22.6732 19.6481 22.5565 19.6486 22.4386ZM2.67935 17.0533C2.3275 17.0533 1.97908 16.9837 1.65401 16.8484C1.32894 16.7131 1.03357 16.5147 0.784765 16.2647C0.535964 16.0147 0.338604 15.7178 0.203954 15.3911C0.0693036 15.0645 0 14.7143 0 14.3607C0 14.0071 0.0693036 13.657 0.203954 13.3303C0.338604 13.0036 0.535964 12.7068 0.784765 12.4567C1.03357 12.2067 1.32894 12.0084 1.65401 11.873C1.97908 11.7377 2.3275 11.6681 2.67935 11.6681C3.38996 11.6681 4.07147 11.9518 4.57394 12.4567C5.07642 12.9617 5.35871 13.6466 5.35871 14.3607C5.35871 15.0748 5.07642 15.7597 4.57394 16.2647C4.07147 16.7697 3.38996 17.0533 2.67935 17.0533ZM2.67935 15.707C2.8553 15.7071 3.02954 15.6723 3.1921 15.6046C3.35467 15.537 3.50238 15.4378 3.6268 15.3128C3.75123 15.1878 3.84993 15.0393 3.91727 14.876C3.98461 14.7126 4.01927 14.5375 4.01927 14.3607C4.01927 14.1839 3.98461 14.0088 3.91727 13.8454C3.84993 13.6821 3.75123 13.5337 3.6268 13.4086C3.50238 13.2836 3.35467 13.1845 3.1921 13.1168C3.02954 13.0492 2.8553 13.0144 2.67935 13.0144C2.32409 13.0145 1.9834 13.1563 1.73221 13.4088C1.48103 13.6613 1.33992 14.0037 1.33992 14.3607C1.33992 14.7177 1.48103 15.0601 1.73221 15.3126C1.9834 15.5651 2.32409 15.707 2.67935 15.707ZM58.0527 11.6681C57.3421 11.6681 56.6606 11.3844 56.1581 10.8794C55.6556 10.3745 55.3733 9.68957 55.3733 8.97544C55.3733 8.26131 55.6556 7.57643 56.1581 7.07146C56.6606 6.5665 57.3421 6.28281 58.0527 6.28281C58.7633 6.28281 59.4448 6.5665 59.9473 7.07146C60.4498 7.57643 60.732 8.26131 60.732 8.97544C60.732 9.68957 60.4498 10.3745 59.9473 10.8794C59.4448 11.3844 58.7633 11.6681 58.0527 11.6681ZM58.0527 10.3218C58.2286 10.3218 58.4029 10.287 58.5654 10.2193C58.728 10.1517 58.8757 10.0525 59.0001 9.92752C59.1246 9.8025 59.2233 9.65407 59.2906 9.49071C59.3579 9.32735 59.3926 9.15226 59.3926 8.97544C59.3926 8.79862 59.3579 8.62353 59.2906 8.46017C59.2233 8.29682 59.1246 8.14839 59.0001 8.02337C58.8757 7.89835 58.728 7.79918 58.5654 7.73154C58.4029 7.6639 58.2286 7.6291 58.0527 7.62913C57.6974 7.62919 57.3567 7.77106 57.1055 8.02354C56.8544 8.27602 56.7132 8.61842 56.7132 8.97544C56.7132 9.33247 56.8544 9.67487 57.1055 9.92735C57.3567 10.1798 57.6974 10.3217 58.0527 10.3218Z"
                    fill="#C4C4C4"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_746_244">
                    <rect width="67" height="44" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className="text-center">
              <h1 className="text-m font-bold text-gray-500">
                You don't have inactive alerts
              </h1>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditNotification;
